
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { useTeams } from '@/hooks/useTeams';

interface CreateTeamModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTeamCreated: () => void;
}

export const CreateTeamModal = ({ open, onOpenChange, onTeamCreated }: CreateTeamModalProps) => {
  const [teamName, setTeamName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { createTeam } = useTeams({ enabled: false }); // Only need createTeam function

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      return;
    }

    setIsCreating(true);
    
    const result = await createTeam(teamName, description);
    
    if (result) {
      // Reset form and close modal
      setTeamName('');
      setDescription('');
      onOpenChange(false);
      onTeamCreated();
    }
    
    setIsCreating(false);
  };

  const handleClose = () => {
    if (!isCreating) {
      setTeamName('');
      setDescription('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" aria-describedby="team-desc">
        <DialogHeader>
          <DialogTitle>Create New Team</DialogTitle>
          <DialogDescription>
            Create a farming team to collaborate with other farmers in your area.
          </DialogDescription>
        </DialogHeader>
        
        <p id="team-desc" className="sr-only">
          Create a new farming team to collaborate with other farmers, share agricultural insights, and work together on farming projects in your area.
        </p>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="teamName" className="block text-sm font-medium text-gray-700 mb-1">
              Team Name *
            </label>
            <Input
              id="teamName"
              placeholder="e.g., Corn Growers Alliance"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              disabled={isCreating}
              maxLength={100}
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <Textarea
              id="description"
              placeholder="Describe what your team is about..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isCreating}
              rows={3}
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateTeam}
            disabled={isCreating || !teamName.trim()}
          >
            {isCreating ? 'Creating...' : 'Create Team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
